import _ from 'lodash'
import { Component, GridPointData, NegativeBottomTops } from '../Types'
import { bottom, height, top } from './compLayoutUtils'

interface EdgePoint {
	y: number
	startingComps: Array<Component>
	endingComps: Array<Component>
}

interface EdgePointsMap {
	[y: number]: EdgePoint
}

//
// Returns: gridPoints: [
//     {y: <number>, startingComps?: [], pushingComps?: [], pushMargin?: <number>}, ...
// ]
//
//  Project all components' points to one map (each y appears once)
//  Transform map into sorted array
//  For each y in the sorted array add a gridPoint to gridPoints array (each y can be added more than one gridPoint)
//      take aside zeroHeight comps
//      add a gridPoint for pushing comps
//          if previous point is pushing --> append and update y
//          else add new gridPoint
//      add new gridPoint for zeroHeight starting comps
//      add new gridPoint for zeroHeight pushing comps
//      add new gridPoint for starting comps
//  Set push margins for pushing gridPoints
//
// @ts-ignore
const bottomConsideringNegativeBottomTop = (component, negativeBottomTops) =>
	bottom(component) - (negativeBottomTops[component.id] || 0)

const accessEdgePointInMap = (y: number, edgePoints: EdgePointsMap) => {
	if (!edgePoints[y]) {
		edgePoints[y] = {
			y,
			startingComps: [],
			endingComps: [],
		}
	}
	return edgePoints[y]
}
const projectComponentsEdgePoints = (
	gridComponents: Array<Component>,
	negativeBottomTops: NegativeBottomTops
): EdgePointsMap =>
	_.reduce(
		gridComponents,
		(edgePointsMap, component): EdgePointsMap => {
			const compTop = top(component)
			const compBottom = bottomConsideringNegativeBottomTop(component, negativeBottomTops)
			accessEdgePointInMap(compTop, edgePointsMap).startingComps.push(component)
			if (height(component) > 0) {
				accessEdgePointInMap(compBottom, edgePointsMap).endingComps.push(component)
			}
			return edgePointsMap
		},
		{} as EdgePointsMap
	)

const addGridPointForStartingComps = (gridPoints: Array<GridPointData>, y: number, startingComps: Array<Component>) => {
	if (_.isEmpty(startingComps)) {
		return
	}
	const newStartingPoint = {
		y,
		startingComps,
	}
	gridPoints.push(newStartingPoint)
}

const addGridPointForPushingComps = (gridPoints: Array<GridPointData>, y: number, endingComps: Array<Component>) => {
	if (_.isEmpty(endingComps)) {
		return
	}

	const lastPoint = _.last(gridPoints)
	const isLastPointPushing = !!_.get(lastPoint, 'pushingComps', null)

	if (isLastPointPushing) {
		// @ts-ignore
		lastPoint.pushingComps.push(...endingComps)
		// @ts-ignore
		lastPoint.y = y
	} else {
		const newPushingPoint = {
			y,
			pushingComps: endingComps,
			pushMargin: 0,
		}
		gridPoints.push(newPushingPoint)
	}
}

const addGridPointsForZeroHeightComps = (
	gridPoints: Array<GridPointData>,
	y: number,
	zeroHeightComponents: Array<Component>
) => {
	addGridPointForStartingComps(gridPoints, y, zeroHeightComponents)
	addGridPointForPushingComps(gridPoints, y, zeroHeightComponents)
}

const setPushMargins = (gridPoints: Array<GridPointData>) => {
	gridPoints.forEach((gridPoint, pointIndex) => {
		const isLastPoint = pointIndex === gridPoints.length - 1
		if (!gridPoint.pushingComps || isLastPoint) {
			return
		}
		const nextGridPoint = gridPoints[pointIndex + 1]
		gridPoint.pushMargin = nextGridPoint.y - gridPoint.y
	})
}

const calculateGridPoints = (
	gridComponents: Array<Component>,
	negativeBottomTops: NegativeBottomTops
): Array<GridPointData> => {
	const edgePointsMap = projectComponentsEdgePoints(gridComponents, negativeBottomTops)
	const sortedEdgePoints = _.sortBy(edgePointsMap, 'y')
	const gridPoints = _.reduce(
		sortedEdgePoints,
		(accGridPoints, edgePoint) => {
			const [zeroHeightComps, startingComps] = _.partition(edgePoint.startingComps, (comp) => height(comp) === 0)
			addGridPointForPushingComps(accGridPoints, edgePoint.y, edgePoint.endingComps)
			addGridPointsForZeroHeightComps(accGridPoints, edgePoint.y, zeroHeightComps)
			addGridPointForStartingComps(accGridPoints, edgePoint.y, startingComps)
			return accGridPoints
		},
		[]
	)

	setPushMargins(gridPoints)
	return gridPoints
}

export default calculateGridPoints
